import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useCallback } from "react";

/**
 * Schedule timeline drag-and-drop shell: wraps the timeline in `@dnd-kit/core`'s
 * {@link DndContext} so scope bars can be dragged and dropped onto grid cells.
 *
 * Configures pointer activation (8px move before drag starts), collision detection
 * that prefers {@link pointerWithin} for precise cell hits and falls back to
 * {@link rectIntersection}, and always-measured droppables for reliable geometry.
 *
 * Renders an optional {@link DragOverlay} for the dragged preview; it uses
 * `pointerEvents: "none"` so the pointer still registers on droppables underneath.
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @param {import('@dnd-kit/core').DragEndEventHandler} [props.onDragEnd]
 * @param {import('@dnd-kit/core').DragOverEventHandler} [props.onDragOver]
 * @param {import('@dnd-kit/core').DragStartEventHandler} [props.onDragStart]
 * @param {import('@dnd-kit/core').DragCancelEventHandler} [props.onDragCancel]
 * @param {import('react').ReactNode} [props.overlay] Node shown in DragOverlay while dragging
 * @returns {JSX.Element}
 */
export default function ScheduleTimelineDnd({
  children,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDragCancel,
  overlay = null,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  /**
   * Prefer pointer-within cell hits; fall back to rect intersection when the pointer is between cells.
   *
   * @param {Object} args dnd-kit collision args.
   * @returns {Array<Object>}
   */
  const collisionDetection = useCallback((args) => {
    const byPointer = pointerWithin(args);
    if (byPointer.length > 0) {
      return byPointer;
    }
    return rectIntersection(args);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      measuring={{
        droppable: { strategy: MeasuringStrategy.Always },
      }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      {children}
      {/* Let pointer events reach grid droppables under the cursor; otherwise pointerWithin never hits cells. */}
      <DragOverlay
        dropAnimation={null}
        style={{ pointerEvents: "none" }}
      >
        {overlay}
      </DragOverlay>
    </DndContext>
  );
}
